import React, { FC } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fal } from '@fortawesome/pro-light-svg-icons';

import SLink from './Link.styled';
import { linkIconSize, linkSizeClass } from './Link.constants';
import Tooltip from '@/presentation/tooltip';
import useKeyPressHandler from '@/hooks/useKeyPressHandler';

export enum LinkType {
  primary = 'primary',
  secondary = 'secondary',
  white = 'white',
}

export interface ILinkProps {
  label: string;
  icon?: string;
  onClick?: string | (() => void);
  size?: 'xs' | 's' | 'm' | 'l' | 'xl';
  disabled?: boolean;
  type?: 'primary' | 'secondary' | 'white';
  noUnderline?: boolean;
  id?: string;
  targetBlank?: boolean;
  title?: string;
  className?: string;
}

const Link: FC<ILinkProps> = ({
  label,
  icon = '',
  onClick = '#',
  size = 'xs',
  disabled = false,
  type = 'primary',
  noUnderline = false,
  id = '',
  targetBlank = false,
  title = '',
  className = '',
}) => {
  const { onKeyPressHandler } = useKeyPressHandler(onClick);

  const getLinkHrefProp = () => {
    if (disabled) {
      return () => void 0;
    }

    return typeof onClick === 'string' ? { href: onClick } : { onClick, onKeyPress: onKeyPressHandler };
  };
  const linkHrefProp = getLinkHrefProp();
  const targetProp = targetBlank ? { target: '_blank' } : null;
  const tabIndex = disabled ? null : { tabIndex: 0 };

  const renderIcon = () => {
    if (!icon) {
      return null;
    }

    return <FontAwesomeIcon icon={fal[icon]} style={{ fontSize: linkIconSize[size] }} />;
  };

  const renderLabel = () => {
    return (
      <Tooltip parentTitle position="top" anchorElement="target" style={{ maxWidth: '300px' }}>
        <span className={size} title={title}>
          {renderIcon()}
          {label}
        </span>
      </Tooltip>
    );
  };

  return (
    <SLink
      aria-roledescription="button"
      id={id}
      {...linkHrefProp}
      {...targetProp}
      {...tabIndex}
      className={`${className} ${linkSizeClass[size]} ${type} ${disabled ? 'disabled' : ''} ${
        noUnderline ? 'no-underline' : ''
      }`}
    >
      {renderLabel()}
    </SLink>
  );
};

export default Link;
